import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StyleSheet, View, Pressable, ActivityIndicator, Platform, Linking, Animated } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { referralApi } from '@/lib/api';
import { trackRetentionEvent, RETENTION_ACTION } from '@/constants/utils/analyticsEvents';
import { useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatCreditBalance } from '@/lib/formatCredits';
import moment from 'moment-mini';

const ACCENT = '#F97316';

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },

  // Hero card — horizontal layout matching EarnCreditsSettings
  heroCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    padding: 18,
    borderRadius: 14,
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.06)' : 'rgba(249, 115, 22, 0.04)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.12)',
  },
  heroIconRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.14)' : 'rgba(249, 115, 22, 0.10)',
  },
  heroTextBlock: {
    flex: 1,
    gap: 4,
  },
  heroStatsPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    marginTop: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.14)' : 'rgba(249, 115, 22, 0.10)',
  },

  // Sections
  section: {
    gap: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
    marginBottom: 2,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },

  // Referral link card
  linkCard: {
    borderRadius: 12,
    backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  linkCardInner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 5,
    paddingVertical: 5,
    gap: 10,
  },
  linkUrl: {
    flex: 1,
    paddingVertical: 6,
  },
  copyButton: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ACCENT,
    minWidth: 84,
    alignItems: 'center',
  },
  copyButtonCopied: {
    backgroundColor: '#10B981',
  },

  // Share row
  shareRow: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 5,
  },
  shareButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  },

  // Stats
  statsRow: {
    flexDirection: 'row',
    gap: 15,
  },
  statCard: {
    flex: 1,
    minWidth: 100,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },
  statHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
  },

  // History
  historyList: {
    gap: 6,
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    gap: 12,
  },
  historyAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  historyBody: {
    flex: 1,
    gap: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    overflow: 'hidden',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 15,
    gap: 8,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },
  emptyIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    marginBottom: 2,
  },

  // How it works — numbered steps
  stepsCard: {
    borderRadius: 12,
    padding: 16,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    gap: 14,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.14)' : 'rgba(249, 115, 22, 0.10)',
    flexShrink: 0,
  },
  stepConnector: {
    position: 'absolute',
    left: 11.5,
    top: 26,
    width: 1,
    height: 14,
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.12)' : 'rgba(249, 115, 22, 0.08)',
  },
  stepTextBlock: {
    flex: 1,
    paddingTop: 2,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 15,
    padding: 15,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
});

const SHARE_CHANNELS = [
  { key: 'twitter', icon: 'share',    label: 'X / Twitter' },
  { key: 'email',   icon: 'envelope', label: 'Email' },
  { key: 'sms',     icon: 'message',  label: 'SMS' },
];

const STATUS_STYLES = {
  completed: { color: '#22c55e', bgDark: 'rgba(34,197,94,0.15)', bgLight: '#dcfce7', icon: 'check' },
  pending:   { color: '#f59e0b', bgDark: 'rgba(245,158,11,0.15)', bgLight: '#fef3c7', icon: 'clock' },
  rejected:  { color: '#ef4444', bgDark: 'rgba(239,68,68,0.15)', bgLight: '#fee2e2', icon: 'xmark' },
};

export default function ReferralSettings() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { addToast } = useToastStore(state => state.actions);
  const { t } = useTranslation();

  const copyScale = useRef(new Animated.Value(1)).current;

  const getShareMessage = useCallback(
    (link) => t('settings.referral.shareMessage').replace('%LINK%', link),
    [t],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const [codeData, statsData] = await Promise.all([
        referralApi.getCode(),
        referralApi.getStats(),
      ]);
      setReferralLink(codeData.referralLink || '');
      setStats({ ...codeData.stats, ...statsData.stats });
      setReferrals(statsData.referrals || []);
    } catch (error) {
      setLoadError(true);
      addToast(error.message || t('settings.referral.failedToLoad'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCopy = useCallback(async () => {
    if (!referralLink) return;
    try {
      await Clipboard.setStringAsync(referralLink);
      trackRetentionEvent(RETENTION_ACTION.REFERRAL_SHARED, 'copy_link');
      setCopied(true);
      Animated.sequence([
        Animated.timing(copyScale, { toValue: 0.92, duration: 80, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(copyScale, { toValue: 1, duration: 120, useNativeDriver: Platform.OS !== 'web' }),
      ]).start();
      setTimeout(() => setCopied(false), 2200);
    } catch {
      addToast(t('settings.referral.failedToCopy'), 'error');
    }
  }, [referralLink, addToast, t, copyScale]);

  const handleShare = useCallback((channel) => {
    if (!referralLink) return;
    trackRetentionEvent(RETENTION_ACTION.REFERRAL_SHARED, channel);
    const message = getShareMessage(referralLink);
    switch (channel) {
      case 'twitter': {
        Linking.openURL(`https://x.com/intent/tweet?text=${encodeURIComponent(message)}`);
        break;
      }
      case 'email': {
        const subject = encodeURIComponent(t('settings.referral.emailSubject'));
        Linking.openURL(`mailto:?subject=${subject}&body=${encodeURIComponent(message)}`);
        break;
      }
      case 'sms': {
        const sep = Platform.OS === 'ios' ? '&' : '?';
        Linking.openURL(`sms:${sep}body=${encodeURIComponent(message)}`);
        break;
      }
    }
  }, [referralLink, t, getShareMessage]);

  if (loadError && !isLoading) return <View style={{ gap: 16, paddingVertical: 24 }}>
    <Text accessibilityRole="alert" color="danger">{t('settings.referral.failedToLoad')}</Text>
    <Button text={t('common.retry')} onPress={fetchData} />
  </View>;

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator color={colors.text_color_secondary} />
      </View>
    );
  }

  const totalCompleted = stats.totalCompleted || 0;
  const totalPending = stats.totalPending || 0;
  const totalCreditsEarned = stats.totalCreditsEarned || 0;
  const creditsPerReferral = stats.creditsPerReferral || 250;
  const maxReferrals = stats.maxReferrals;

  const getStatus = (status) => STATUS_STYLES[status] || {
    color: colors.text_color_tertiary,
    bgDark: 'rgba(156,163,175,0.15)',
    bgLight: '#f3f4f6',
    icon: 'question',
  };

  return (
    <View style={styles.container}>
      {/* Hero card */}
      <View style={styles.heroCard}>
        <View style={styles.heroIconRing}>
          <Icon name="gift" size={24} color={ACCENT} />
        </View>
        <View style={styles.heroTextBlock}>
          <Text size="l" weight="bold" color="primary">
            {t('settings.referral.referAFriend')}
          </Text>
          <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>
            {t('settings.referral.shareDescription').replace('%CREDITS%', creditsPerReferral)}
          </Text>
          {totalCreditsEarned > 0 && (
            <View style={styles.heroStatsPill}>
              <Icon name="coins" size={11} color={ACCENT} />
              <Text size="xs" weight="bold" style={{ color: ACCENT }}>
                {formatCreditBalance(totalCreditsEarned)} earned
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Referral link */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="link" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.referral.yourReferralLink')}
            </Text>
          </View>
        </View>
        <View style={styles.linkCard}>
          <View style={styles.linkCardInner}>
            <Text size="s" color="primary" numberOfLines={1} style={styles.linkUrl}>
              {referralLink || t('settings.referral.loading')}
            </Text>
            <Animated.View style={{ transform: [{ scale: copyScale }] }}>
              <Pressable
                style={[styles.copyButton, copied && styles.copyButtonCopied]}
                onPress={handleCopy}
              >
                <Text size="s" weight="semibold" color="white">
                  {copied ? '✓ Copied' : t('settings.referral.copy')}
                </Text>
              </Pressable>
            </Animated.View>
          </View>
        </View>

        <View style={styles.shareRow}>
          {SHARE_CHANNELS.map((ch) => (
            <Pressable
              key={ch.key}
              style={styles.shareButton}
              onPress={() => handleShare(ch.key)}
            >
              <Icon name={ch.icon} size={13} color={colors.text_color_secondary} />
              <Text size="xs" weight="medium" color="secondary">{ch.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Stats */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="chart-simple" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.referral.yourStats')}
            </Text>
          </View>
        </View>
        <View style={styles.statsRow}>
          <View style={styles.statCard}>
            <View style={styles.statHeader}>
              <Icon name="check" size={11} color="#22c55e" solid />
              <Text size="xs" color="secondary">{t('settings.referral.successful')}</Text>
            </View>
            <Text size="l" weight="bold">{totalCompleted}</Text>
          </View>
          <View style={styles.statCard}>
            <View style={styles.statHeader}>
              <Icon name="clock" size={11} color="#f59e0b" solid />
              <Text size="xs" color="secondary">{t('settings.referral.pending')}</Text>
            </View>
            <Text size="l" weight="bold">{totalPending}</Text>
          </View>
          <View style={styles.statCard}>
            <View style={styles.statHeader}>
              <Icon name="coins" size={11} color={ACCENT} solid />
              <Text size="xs" color="secondary">{t('settings.referral.creditsEarned')}</Text>
            </View>
            <Text size="l" weight="bold">{formatCreditBalance(totalCreditsEarned)}</Text>
            <Text size="xxs" color="tertiary" style={{ marginTop: 2 }}>
              +{creditsPerReferral} per referral
            </Text>
          </View>
        </View>
      </View>

      {/* Referral history */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="clock-rotate-left" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.referral.referralHistory')}
            </Text>
          </View>
          {referrals.length > 0 && (
            <View style={{
              paddingHorizontal: 7,
              paddingVertical: 1,
              borderRadius: 10,
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            }}>
              <Text size="xxs" weight="semibold" color="secondary">{referrals.length}</Text>
            </View>
          )}
        </View>

        {referrals.length === 0 ? (
          <View style={styles.emptyState}>
            <View style={styles.emptyIcon}>
              <Icon name="user-plus" size={16} color={colors.text_color_tertiary} />
            </View>
            <Text size="s" color="secondary">{t('settings.referral.noReferrals')}</Text>
            <Text size="xs" color="tertiary">Share your link to get started</Text>
          </View>
        ) : (
          <View style={styles.historyList}>
            {referrals.map((referral) => {
              const status = getStatus(referral.status);
              return (
                <View key={referral.id} style={styles.historyItem}>
                  <View style={[styles.historyAvatar, {
                    backgroundColor: isDark
                      ? `${status.color}18`
                      : `${status.color}12`,
                  }]}>
                    <Icon name={status.icon} size={12} color={status.color} />
                  </View>
                  <View style={styles.historyBody}>
                    <Text size="s" weight="medium" color="primary">
                      {t('settings.referral.referralNumber').replace('%ID%', referral.id?.toString().slice(-6) || '—')}
                    </Text>
                    <Text size="xs" color="tertiary">
                      {referral.createdAt ? moment(referral.createdAt).fromNow() : '—'}
                    </Text>
                  </View>
                  <View style={[styles.statusBadge, {
                    backgroundColor: isDark ? status.bgDark : status.bgLight,
                  }]}>
                    <Text size="xs" weight="medium" style={{ color: status.color }}>
                      {referral.status ? referral.status.charAt(0).toUpperCase() + referral.status.slice(1) : '—'}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* How it works — numbered steps */}
      <View style={styles.stepsCard}>
        <Text size="s" weight="semibold" color="primary" style={{ marginBottom: 2 }}>
          {t('settings.referral.howItWorks')}
        </Text>

        <View style={styles.stepRow}>
          <View>
            <View style={styles.stepNumber}>
              <Text size="xxs" weight="bold" style={{ color: ACCENT }}>1</Text>
            </View>
            <View style={styles.stepConnector} />
          </View>
          <View style={styles.stepTextBlock}>
            <Text size="s" weight="medium" color="primary">Share your link</Text>
            <Text size="xs" color="secondary" style={{ lineHeight: 17, marginTop: 1 }}>
              {t('settings.referral.step1')}
            </Text>
          </View>
        </View>

        <View style={styles.stepRow}>
          <View>
            <View style={styles.stepNumber}>
              <Text size="xxs" weight="bold" style={{ color: ACCENT }}>2</Text>
            </View>
            <View style={styles.stepConnector} />
          </View>
          <View style={styles.stepTextBlock}>
            <Text size="s" weight="medium" color="primary">Friend signs up</Text>
            <Text size="xs" color="secondary" style={{ lineHeight: 17, marginTop: 1 }}>
              {t('settings.referral.step2')}
            </Text>
          </View>
        </View>

        <View style={styles.stepRow}>
          <View>
            <View style={styles.stepNumber}>
              <Text size="xxs" weight="bold" style={{ color: ACCENT }}>3</Text>
            </View>
          </View>
          <View style={styles.stepTextBlock}>
            <Text size="s" weight="medium" color="primary">You both earn credits</Text>
            <Text size="xs" color="secondary" style={{ lineHeight: 17, marginTop: 1 }}>
              {t('settings.referral.step3').replace('%CREDITS%', creditsPerReferral)}
            </Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      {maxReferrals ? (
        <View style={styles.footer}>
          <Icon name="circle-info" size={12} color={colors.text_color_secondary} style={{ marginTop: 2 }} />
          <Text size="xs" color="secondary" style={{ flex: 1, lineHeight: 18 }}>
            {t('settings.referral.maxReferrals').replace('%MAX%', maxReferrals)}
          </Text>
        </View>
      ) : null}
    </View>
  );
}
